/**
 * Make every native date/time input a "click anywhere to pick" control.
 *
 * By default Chrome/Edge only open the calendar (or clock) from the small indicator icon at
 * the right edge of `<input type="date">`; a click anywhere else just focuses the field and
 * highlights its first segment (the day turns blue) so it can be typed into. Users read that
 * as "nothing happened". The portal has ~180 such inputs (`CustomDateTimeField`,
 * `CustomField type='date'`, the dynamic-form `Date`/`DateTime` fields, and raw `<input>`s),
 * so instead of wiring each one, `installNativePickerAutoOpen()` registers a single delegated
 * `mousedown` listener on `document` that opens the picker with `showPicker()` for any of
 * them.
 *
 * Behaviour notes:
 * - Runs on `mousedown` (not `click`) and calls `preventDefault()` so the input is not focused
 *   and no segment is left highlighted — matches the flow users expect from flatpickr fields.
 * - `showPicker()` must run inside a user gesture, throws for read-only/disabled inputs, and
 *   is missing on older Safari. In every one of those cases we leave the default behaviour
 *   alone, so the field still works as a normal typed input.
 * - Skips events another handler already `preventDefault()`ed, so a component that manages
 *   its own picker keeps control.
 * - Keyboard entry is untouched: Tab into the field and type.
 */

/** Native input types whose picker is worth auto-opening. */
export const NATIVE_PICKER_INPUT_TYPES: ReadonlySet<string> = new Set([
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
])

/**
 * Open the native picker for `input` if the browser allows it right now.
 *
 * @returns `true` when the picker was opened, `false` when the default behaviour should be
 *   left alone (unsupported browser, read-only/disabled field, no user gesture, …).
 */
export const openNativePicker = (input: HTMLInputElement): boolean => {
  if (typeof input.showPicker !== 'function') return false
  if (input.disabled || input.readOnly) return false
  try {
    input.showPicker()
    return true
  } catch {
    // NotAllowedError (no gesture / already open), SecurityError (cross-origin iframe),
    // InvalidStateError (not mutable) — fall back to the browser's normal click handling.
    return false
  }
}

/**
 * Delegated handler: opens the picker when the mousedown landed on a native date/time input.
 * Exported for tests; use {@link installNativePickerAutoOpen} in app code.
 */
export const handleNativePickerMouseDown = (event: MouseEvent): void => {
  if (event.defaultPrevented) return
  // Only the primary button — right-click should still show the context menu.
  if (event.button !== 0) return
  const target = event.target
  if (!(target instanceof HTMLInputElement)) return
  if (!NATIVE_PICKER_INPUT_TYPES.has(target.type)) return
  if (openNativePicker(target)) {
    event.preventDefault()
  }
}

let installed = false

/**
 * Register the document-level listener once. Call at bootstrap (App.tsx).
 *
 * @returns a disposer that removes the listener (used by tests).
 */
export const installNativePickerAutoOpen = (): (() => void) => {
  if (installed || typeof document === 'undefined') return () => {}
  installed = true
  document.addEventListener('mousedown', handleNativePickerMouseDown)
  return () => {
    document.removeEventListener('mousedown', handleNativePickerMouseDown)
    installed = false
  }
}
